// Updated HistoryServiceOrders.tsx with server-side filtering
import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { GenericTable } from "@shared/components/GenericTable";
import {
  FileText,
  CheckCircle,
  XCircle,
  Search,
  Eye,
  Printer,
  Filter,
  TrendingUp,
  Zap,
  User,
  Settings,
} from "lucide-react";

// Updated imports - using new filtering hooks
import {
  useDownloadReport,
  useHistoryServiceOrdersExport,
  useHistoryServiceOrdersWithFilters,
  useHistorySummary,
} from "../../hooks";
import { serviceOrderFilters, TransformedServiceOrder } from "../../types";

// Import existing components
import { ServiceOrderFilterPanel } from "../../../dispatcher/components/filters/filters";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { getPriorityColor, getStatusColor, formatDate } from "../../../utils";
import { toast } from "@shared/hooks/use-toast";

// Utility functions
const getAssociationIcon = (association: string) => {
  const assocLower = association?.toLowerCase();
  switch (assocLower) {
    case "meter":
      return <Zap className="h-4 w-4" />;
    case "consumer":
      return <User className="h-4 w-4" />;
    case "asset":
      return <Settings className="h-4 w-4" />;
    default:
      return null;
  }
};

export function HistoryServiceOrders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // =================== API DATA FETCHING WITH FILTERS ===================
  const {
    serviceOrders,
    isLoading,
    error,
    pagination,
    currentFilters,
    handleSearch,
    handlePageChange,
    handleApplyFilters,
    handleResetFilters,
    refetch,
  } = useHistoryServiceOrdersWithFilters();
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = useHistorySummary();
  const downloadCSV = useDownloadReport();

  const { exportCSV, isExporting } =
    useHistoryServiceOrdersExport(currentFilters);

  const handleExport = () => {
    exportCSV();
  };

  // Parse current filter values from URL for display
  const searchQuery = searchParams.get("query") || "";
  const statusFilter = searchParams.get("status") || "all";
  const serviceAssociationFilter =
    searchParams.get("serviceAssociation") || "all";
  const priorityFilter = searchParams.get("priority") || "all";
  const areaFilter = searchParams.get("area") || "all";
  const slaFilter = searchParams.get("sla") || "all";

  // Parse sort config from URL
  const sortConfig = useMemo(
    () => ({
      field: searchParams.get("sortField") || "createdOn",
      direction:
        (searchParams.get("sortDirection") as "asc" | "desc") || "desc",
    }),
    [searchParams]
  );

  // =================== KPI CALCULATIONS ===================
  const kpis = useMemo(() => {
    return {
      totalOrders: summaryData?.total || 0,
      completedOrders: summaryData?.completed || 0,
      approvedOrders: summaryData?.approved || 0,
      rejectedOrders: summaryData?.rejected || 0,
    };
  }, [summaryData]);
  const historyServiceOrderKpiCards = [
    {
      title: "Total Service Orders",
      count: kpis.totalOrders,
      trendSummary: "All service orders in the system history",
      trendDirection: "up" as const,
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Completed Service Orders",
      count: kpis.completedOrders,
      trendSummary: "Orders successfully completed by technicians",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Approved Service Orders",
      count: kpis.approvedOrders,
      trendSummary: "Orders approved and finalized",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-purple-500" />,
    },
    {
      title: "Rejected Service Orders",
      count: kpis.rejectedOrders,
      trendSummary: "Orders rejected during processing",
      trendDirection: "down" as const,
      icon: <XCircle className="h-8 w-8 text-red-500" />,
    },
  ];
  // =================== EVENT HANDLERS ===================
  const handleViewClick = (order: any) => {
    const association = order.serviceAssociation?.toLowerCase();

    switch (association) {
      case "meter":
        navigate(
          `/service-orders/dispatcher/history-service-order/${order.id}`
        );
        break;
      case "consumer":
        navigate(
          `/service-orders/dispatcher/history-service-order/${order.id}`
        );
        break;
      case "asset":
        navigate(
          `/service-orders/dispatcher/history-asset-service-order/Asset/${order.id}`
        );
        break;
      case "facility":
        navigate(
          `/service-orders/dispatcher/history-asset-service-order/Facility/${order.id}`
        );
        break;
      case "system":
        navigate(
          `/service-orders/dispatcher/history-asset-service-order/System/${order.id}`
        );
        break;
      case "network":
        navigate(
          `/service-orders/dispatcher/history-asset-service-order/Network/${order.id}`
        );
        break;
      default:
        navigate(
          `/service-orders/dispatcher/history-service-order/${order.id}`
        );
        break;
    }
  };

  // =================== TABLE CONFIGURATION ===================
  const tableColumns = [
    {
      key: "soId",
      header: "SO ID",
      sortable: true,
      render: (value: string) => (
        <span className="text-blue-600 font-medium">{value}</span>
      ),
    },
    {
      key: "soName",
      header: "SO Name",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-900 font-medium">{value}</span>
      ),
    },
    {
      key: "referenceNo",
      header: "Reference No.",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "serviceAssociation",
      header: "Service Association",
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {getAssociationIcon(value)}
          <span className="ml-1">{value}</span>
        </span>
      ),
    },
    {
      key: "technician",
      header: "Technician",
      sortable: true,
      render: (value: any) => {
        if (!value) return <span className="text-gray-500">Unassigned</span>;
        if (typeof value === "string")
          return <span className="text-gray-700">{value}</span>;
        return (
          <div>
            <div className="font-medium text-gray-900">
              {value.name || "Unassigned"}
            </div>
            <div className="text-sm text-gray-500">({value.id || "N/A"})</div>
          </div>
        );
      },
    },
    {
      key: "priority",
      header: "Priority",
      sortable: true,
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => getPriorityColor(value),
      },
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge" as const,
      badgeConfig: {
        getVariant: (value: any) => {
          const statusStr = value?.toString()?.toLowerCase() || "";
          switch (statusStr) {
            case "approved":
            case "completed":
              return "default";
            case "rejected":
              return "destructive";
            case "in progress":
            case "assigned":
              return "secondary";
            default:
              return "secondary";
          }
        },
        getClassName: (value: any) => {
          const statusStr = value?.toString()?.toLowerCase() || "";
          switch (statusStr) {
            case "approved":
            case "completed":
              return "bg-green-100 text-green-800 border-green-200";
            case "rejected":
              return "bg-red-100 text-red-800 border-red-200";
            case "in progress":
            case "assigned":
              return "bg-orange-100 text-orange-800 border-orange-200";
            default:
              return "";
          }
        },
      },
    },
    {
      key: "slaCompliance",
      header: "SLA Compliance",
      sortable: true,
      render: (value: string, row: any) => {
        value;
      },
    },
    {
      key: "createdOn",
      header: "Created On",
      sortable: true,
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "assignDate",
      header: "Assigned",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-700">{value ? value : "NA"}</span>
      ),
    },
    {
      key: "eta",
      header: "Expected",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-700">{value ? value : "NA"}</span>
      ),
    },
    {
      key: "completedOn",
      header: "Completed",
      sortable: true,
      render: (value: string, row: any) => {
        // Check multiple possible completed date fields
        const completedDate = value || row.completed || row.completedDate;
        return (
          <span className="text-gray-700">
            {completedDate && completedDate !== "-" ? completedDate : "-"}
          </span>
        );
      },
    },
  ];

  // GenericTable actions configuration
  const tableActions = [
    {
      label: "View Details",
      onClick: (row: any) => handleViewClick(row),
      icon: "Eye" as const,
      variant: "ghost" as const,
    },
    {
      label: "Print Report",
      onClick: (order: TransformedServiceOrder) =>
        downloadCSV.mutateAsync({ reportId: order.id }),
      icon: "Printer" as const,
      variant: "ghost" as const,
    },
  ];

  // Sorting configuration - now from URL
  // Removed local sort state - using URL-based sorting

  // Create current filters object for filterPanel
  const filterPanelFilters: Partial<serviceOrderFilters> = useMemo(
    () => ({
      status: statusFilter !== "all" ? statusFilter : undefined,
      serviceAssociation:
        serviceAssociationFilter !== "all"
          ? serviceAssociationFilter
          : undefined,
      priority: priorityFilter !== "all" ? priorityFilter : undefined,
      area: areaFilter !== "all" ? areaFilter : undefined,
      sla_filter: slaFilter !== "all" ? slaFilter : undefined,
      search_data: searchQuery || undefined,
    }),
    [
      statusFilter,
      serviceAssociationFilter,
      priorityFilter,
      areaFilter,
      slaFilter,
      searchQuery,
    ]
  );
  useEffect(() => {
    if (downloadCSV.isPending) {
      toast({
        title: "Report Downloading",
        description: "Your service order report is downloading.",
        variant: "default",
        duration: 3000,
      });
    }
    if (downloadCSV.isSuccess) {
      toast({
        title: "Report Downloaded Successfully",
        description: "Your service order report has been downloaded.",
        variant: "default",
        duration: 3000,
      });
    }

    if (downloadCSV.isError) {
      toast({
        title: "Download Failed",
        description:
          downloadCSV.error?.message ||
          "Failed to download report. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  }, [downloadCSV.isSuccess, downloadCSV.isError, downloadCSV.isPending]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="page-subheading">Service Order History</h2>
        <p className="page-subtitle">Manage and track all service orders</p>
      </div>

      {/* KPI Cards */}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {historyServiceOrderKpiCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            trendDirection={card.trendDirection}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Service Orders Table */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Service Orders ({pagination?.totalItems || 0})
        </h3>

        <GenericTable
          data={serviceOrders}
          columns={tableColumns}
          actions={tableActions}
          actionsType="dropdown"
          rowKey="soId"
          sortConfig={sortConfig}
          loading={isLoading}
          // Search integration like UserTable
          onSearch={handleSearch}
          searchPlaceholder="Search service orders by SO ID, Name..."
          searchParamName="query"
          searchDebounceMs={300}
          onExport={handleExport}
          exportLoading={isExporting}
          // Filter panel integration like UserTable
          filterPanel={
            <ServiceOrderFilterPanel
              currentFilters={filterPanelFilters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              orderType="history"
            />
          }
          // Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          emptyMessage="No service orders found matching your criteria"
          filters={
            <div className="text-sm text-muted-foreground">
              {statusFilter !== "all" && <span>Status: {statusFilter} | </span>}
              {serviceAssociationFilter !== "all" && (
                <span>Association: {serviceAssociationFilter} | </span>
              )}
              {slaFilter !== "all" && <span>SLA: {slaFilter} | </span>}
              {priorityFilter !== "all" && (
                <span>Priority: {priorityFilter}</span>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
}
